import { useState, useEffect, useMemo } from 'react';
import type { WhenSheetProps } from '../types';
import { useClientTranslation } from '@/lib/core/i18n';
import { cn } from '@/lib/core/utils/cn';
import { formatWheelDay } from '@/lib/core/utils/dateTime';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { WheelPicker, type WheelPickerItemStyle } from '@/components/ui/wheel-picker';
import { Icon } from '@/components/ui/icon';
import { useLocaleStore } from '@/lib/core/stores';
import { useApp } from '@/lib/core/providers/appProvider';

type ScheduleMode = 'pickup' | 'arrive';

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function dayOnly(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function wheelItemClassName(style: WheelPickerItemStyle) {
  return {
    className: 'font-pp-neue-montreal select-none whitespace-nowrap',
    style: {
      fontSize: style.fontSize,
      opacity: style.opacity,
      color: '#333',
      transition: 'font-size 80ms ease-out, opacity 80ms ease-out',
    },
  };
}

export function WhenSheet({ isOpen, onClose, cmsProps, booking, onBookingChange }: WhenSheetProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);
  const { appSettings } = useApp();

  const allowMultipleModes = false;
  const [mode, setMode] = useState<ScheduleMode>('pickup');
  const [dayIndex, setDayIndex] = useState(0);
  const [hour, setHour] = useState(0);
  const [minute, setMinute] = useState(0);

  const minimumPreorderMinutes = (appSettings?.properties?.minimumPreorderMinutes as unknown as number) ?? 60;
  const maximumPreorderDays = (appSettings?.properties?.maximumPreorderDays as unknown as number) ?? 30;

  // Wheels are bounded to [now + minimumPreorderMinutes, now + maximumPreorderDays] —
  // matches the "book a car in advance" business rule (Onde also won't dispatch a
  // pre-booking outside its own tariff-level lead-time window, but the picker
  // shouldn't let the user select something outside that range in the first place).
  const dayItems = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return Array.from(
      { length: maximumPreorderDays + 1 },
      (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
    );
  }, [isOpen, maximumPreorderDays]);

  useEffect(() => {
    if (!isOpen) return;
    setMode('pickup');

    const minInstant = new Date(Date.now() + minimumPreorderMinutes * 60 * 1000);
    const initialDate = booking.date ?? minInstant;
    setDayIndex(
      Math.max(
        0,
        dayItems.findIndex(d => isSameDay(d, initialDate))
      )
    );

    const [initialHour, initialMinute] = booking.time
      ? booking.time.split(':').map(Number)
      : [minInstant.getHours(), minInstant.getMinutes()];
    setHour(initialHour);
    setMinute(initialMinute);
  }, [isOpen, dayItems]);

  // Enforce the lead-time window as the wheels move: jump the day forward/back if the
  // selected one falls outside [minInstant, maxInstant] (e.g. it's 23:30 and "today" is
  // selected), and clamp the hour/minute on the two boundary days themselves.
  useEffect(() => {
    if (!isOpen) return;
    const selectedDay = dayItems[dayIndex];
    if (!selectedDay) return;

    const minInstant = new Date(Date.now() + minimumPreorderMinutes * 60 * 1000);
    const maxInstant = new Date(Date.now() + maximumPreorderDays * 24 * 60 * 60 * 1000);

    if (selectedDay < dayOnly(minInstant)) {
      const minDayIndex = dayItems.findIndex(d => isSameDay(d, minInstant));
      if (minDayIndex >= 0) setDayIndex(minDayIndex);
      return;
    }

    if (selectedDay > dayOnly(maxInstant)) {
      const maxDayIndex = dayItems.findIndex(d => isSameDay(d, maxInstant));
      setDayIndex(maxDayIndex >= 0 ? maxDayIndex : dayItems.length - 1);
      return;
    }

    if (isSameDay(selectedDay, minInstant)) {
      const belowFloor =
        hour < minInstant.getHours() || (hour === minInstant.getHours() && minute < minInstant.getMinutes());
      if (belowFloor) {
        setHour(minInstant.getHours());
        setMinute(minInstant.getMinutes());
        return;
      }
    }

    if (isSameDay(selectedDay, maxInstant)) {
      const aboveCeiling =
        hour > maxInstant.getHours() || (hour === maxInstant.getHours() && minute > maxInstant.getMinutes());
      if (aboveCeiling) {
        setHour(maxInstant.getHours());
        setMinute(maxInstant.getMinutes());
      }
    }
  }, [isOpen, dayIndex, hour, minute, dayItems, minimumPreorderMinutes, maximumPreorderDays]);

  const handleSelect = () => {
    const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    onBookingChange({ date: dayItems[dayIndex], time });
    onClose();
  };

  const title = cmsProps?.title ?? t('App.Labels.When');
  const subtitle = cmsProps?.subtitle ?? t('App.Labels.SelectDate');

  return (
    <Drawer
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DrawerContent className="border-jncquoi-neutral-200 border-t-[0.5px]">
        <DrawerHeader className="shrink-0 gap-2 pt-8 pb-3">
          <div className="flex items-center gap-8">
            <DrawerTitle className="flex-1 text-[32px] font-medium tracking-[0.5px]">{title}</DrawerTitle>
            <DrawerClose
              className="flex size-11 cursor-pointer items-center justify-center rounded-full"
              aria-label="Close"
            >
              <Icon
                name="xmark"
                width={24}
                height={24}
                strokeWidth={1.5}
                className="text-jncquoi-neutral-1000"
              />
            </DrawerClose>
          </div>
          <DrawerDescription className="text-[16px] text-[#707070]">{subtitle}</DrawerDescription>
        </DrawerHeader>
        <div data-base-ui-swipe-ignore className="flex-1 overflow-y-auto">
          <div className="flex flex-col gap-8 pt-4 pb-6">
            {allowMultipleModes && (
              <div className="flex justify-center">
                <div className="flex items-center gap-4 rounded-[20px] bg-[#f4f4f4] p-1">
                  {(['pickup', 'arrive'] as ScheduleMode[]).map(modeKey => (
                    <button
                      key={modeKey}
                      type="button"
                      onClick={() => setMode(modeKey)}
                      className={cn(
                        'font-pp-neue-montreal h-[32px] rounded-[20px] px-4 py-1.5 text-[14px] tracking-[0.5px] transition-all duration-200',
                        mode === modeKey
                          ? 'bg-white font-normal text-[#1a1a1a]'
                          : 'bg-[#f4f4f4] font-semibold text-[#1a1a1a]'
                      )}
                    >
                      {modeKey === 'pickup' ? t('App.Labels.PickupAt') : t('App.Labels.ArriveAt')}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="relative px-4">
              <div
                className="pointer-events-none absolute inset-x-6 z-0 rounded-lg bg-[#eaeaea]"
                style={{ top: 31 * 3, height: 34 }}
              />
              <div className="relative z-10 flex w-full justify-center">
                <div className="flex w-full items-center justify-center gap-8">
                  <WheelPicker
                    className="flex min-w-0"
                    items={dayItems}
                    index={dayIndex}
                    onChange={setDayIndex}
                    showHighlight={false}
                    align="end"
                    renderItem={(day, style) => (
                      <span {...wheelItemClassName(style)}>
                        {isSameDay(day, dayItems[0]) ? t('App.Labels.Today') : formatWheelDay(day, locale)}
                      </span>
                    )}
                  />
                  <WheelPicker
                    className="flex min-w-0"
                    items={HOURS}
                    index={hour}
                    onChange={setHour}
                    showHighlight={false}
                    renderItem={(h, style) => <span {...wheelItemClassName(style)}>{h}</span>}
                  />
                  <WheelPicker
                    className="flex min-w-0"
                    items={MINUTES}
                    index={minute}
                    onChange={setMinute}
                    showHighlight={false}
                    renderItem={(m, style) => (
                      <span {...wheelItemClassName(style)}>{String(m).padStart(2, '0')}</span>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 w-full shrink-0 border-t-[0.5px] border-[#c8c8c8] bg-white px-4 pt-6 pb-10.5">
            <Button
              variant="primary-solid"
              palette="dark"
              size="lg"
              className="w-full"
              onClick={handleSelect}
            >
              {cmsProps?.selectButtonLabel ?? t('App.Labels.Select')}
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
